import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/base/SEOHead";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { articles } from "@/mocks/articles";
import { brandInfo, faqs } from "@/mocks/brand";
import { useState } from "react";

const featuredArticles = articles.filter((a) => a.featured);

const categoryCards = [
  {
    id: "cat-1",
    name: "洛丽塔服饰文化",
    slug: "lolita-culture",
    description: "深入探索洛丽塔时尚的起源、流派演变与文化内涵，从维多利亚时代的浪漫到现代街头的甜美演绎。",
    image: "https://readdy.ai/api/search-image?query=Elegant%20Lolita%20fashion%20dress%20with%20lace%20and%20ribbons%2C%20vintage%20inspired%20doll%20like%20silhouette%2C%20soft%20pastel%20background%20with%20scattered%20rose%20petals%2C%20delicate%20feminine%20aesthetic%2C%20editorial%20fashion%20photography%20with%20dreamy%20atmosphere%2C%20high%20detail%20fabric%20texture&width=600&height=400&seq=cat-card-lolita-culture&orientation=landscape",
    articleCount: 8
  },
  {
    id: "cat-2",
    name: "甜美系穿搭",
    slug: "sweet-style",
    description: "掌握甜美系穿搭的核心法则，从色彩搭配到层次叠穿，打造让人心动的梦幻造型。",
    image: "https://readdy.ai/api/search-image?query=Sweet%20kawaii%20fashion%20outfit%20with%20pink%20dress%20and%20frills%2C%20cute%20accessories%20with%20bows%2C%20soft%20cotton%20candy%20colored%20background%2C%20dreamy%20pastel%20aesthetic%2C%20whimsical%20and%20playful%20mood%2C%20editorial%20fashion%20photography%2C%20high%20detail&width=600&height=400&seq=cat-card-sweet-style&orientation=landscape",
    articleCount: 10
  },
  {
    id: "cat-3",
    name: "日系少女风",
    slug: "japanese-girly",
    description: "解锁日系少女风格的穿搭密码，从原宿街拍到杂志封面，找到属于你的日系灵感。",
    image: "https://readdy.ai/api/search-image?query=Japanese%20harajuku%20girl%20fashion%20street%20style%2C%20layered%20outfit%20with%20pleated%20skirt%20and%20cute%20socks%2C%20urban%20Tokyo%20background%20with%20soft%20sunlight%2C%20youthful%20and%20trendy%20aesthetic%2C%20editorial%20fashion%20photography%2C%20clean%20composition&width=600&height=400&seq=cat-card-japanese-girly&orientation=landscape",
    articleCount: 7
  },
  {
    id: "cat-4",
    name: "复古蕾丝搭配",
    slug: "vintage-lace",
    description: "蕾丝、荷叶边、珍珠扣——复古元素在现代穿搭中的巧妙运用，优雅中不失少女感。",
    image: "https://readdy.ai/api/search-image?query=Vintage%20lace%20fashion%20detail%20with%20pearl%20buttons%20and%20delicate%20embroidery%2C%20antique%20inspired%20blouse%20with%20ruffle%20collar%2C%20warm%20cream%20background%20with%20dried%20flowers%2C%20romantic%20nostalgic%20aesthetic%2C%20editorial%20photography%2C%20soft%20natural%20light&width=600&height=400&seq=cat-card-vintage-lace&orientation=landscape",
    articleCount: 6
  },
  {
    id: "cat-5",
    name: "配件搭配",
    slug: "accessories",
    description: "蝴蝶结、发带、手袋、袜子——精致配件是萝莉风格的灵魂，学会用细节提升整体造型。",
    image: "https://readdy.ai/api/search-image?query=Cute%20lolita%20accessories%20collection%20with%20hair%20bows%20headbands%20and%20lace%20gloves%2C%20delicate%20handbag%20and%20frilly%20socks%20arranged%20on%20vanity%20table%2C%20soft%20pink%20background%20with%20fairy%20lights%2C%20feminine%20and%20detail%20oriented%20aesthetic%2C%20editorial%20still%20life%20photography&width=600&height=400&seq=cat-card-accessories&orientation=landscape",
    articleCount: 5
  }
];

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: brandInfo.name,
  url: "https://lolitastyle.cn",
  description: brandInfo.description,
  potentialAction: {
    "@type": "SearchAction",
    target: "https://lolitastyle.cn/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: brandInfo.name,
  url: "https://lolitastyle.cn",
  description: brandInfo.description,
  email: brandInfo.email,
  address: {
    "@type": "PostalAddress",
    addressLocality: "上海",
    addressCountry: "CN",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="萝莉风格时尚穿搭指南 | 洛丽塔服饰文化与甜美穿搭全解析"
        description="探索萝莉风格的时尚世界，从洛丽塔服饰文化、甜美穿搭技巧到配件搭配，全面解析萝莉美学的起源与现代演绎，助你打造属于自己的独特风格。"
        keywords="萝莉,萝莉风格穿搭,洛丽塔时尚,甜美系穿搭,日系少女风,萝莉服饰文化,复古蕾丝搭配"
        canonicalUrl="https://lolitastyle.cn"
        ogImage="https://readdy.ai/api/search-image?query=Elegant%20Lolita%20fashion%20collage%20with%20pastel%20dresses%20lace%20and%20ribbons%2C%20soft%20pink%20and%20cream%20aesthetic%2C%20editorial%20fashion%20design%2C%20dreamy%20feminine%20style&width=1200&height=630&seq=og-home&orientation=landscape"
      />

      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dreamy%20Lolita%20fashion%20aesthetic%20collage%2C%20beautiful%20pastel%20pink%20and%20cream%20dress%20with%20lace%20details%2C%20scattered%20rose%20petals%2C%20soft%20dreamy%20atmosphere%2C%20elegant%20feminine%20editorial%20fashion%20photography%2C%20artistic%20composition&width=1800&height=1200&seq=hero-bg&orientation=landscape"
          alt="萝莉风格时尚穿搭指南"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "var(--font-heading)" }}>
            探索<span className="text-primary-300">萝莉风格</span>的
            <br className="md:hidden" />
            时尚世界
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed">
            从洛丽塔服饰文化、甜美穿搭技巧到配件搭配，全面解析萝莉美学的起源与现代演绎，助你打造属于自己的独特风格。
          </p>
          <div className="flex items-center gap-4 mt-8">
            <Link
              to="/category/lolita-culture"
              className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始探索
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 rounded-full border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            探索风格分类
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10 max-w-xl mx-auto">
            从洛丽塔文化到日常甜美穿搭，五大分类带你全面了解萝莉时尚的方方面面
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categoryCards.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group relative rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                <div className="p-4">
                  <h3 className="text-base font-semibold text-foreground-950 mb-1 whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-2">{cat.description}</p>
                  <span className="inline-block mt-2 text-xs text-accent-500 font-medium">{cat.articleCount} 篇文章</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            精选文章推荐
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10 max-w-xl mx-auto">
            为你精选最受欢迎的穿搭指南和文化科普
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-accent-500 text-white text-xs font-medium">
                    {article.categoryName}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.author}</span>
                    <span>{article.date} · {article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/sweet-style"
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="w-full lg:w-1/2 relative h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20cozy%20fashion%20studio%20with%20lolita%20dresses%20on%20mannequins%2C%20soft%20pastel%20decor%2C%20warm%20natural%20light%2C%20creative%20workspace%20with%20fabric%20swatches%20and%20sketches%2C%20editorial%20interior%20photography%2C%20feminine%20aesthetic&width=800&height=600&seq=about-preview&orientation=landscape"
                alt="关于我们"
                className="w-full h-full object-cover object-top rounded-lg"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                关于<span className="text-primary-500">我们</span>
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {brandInfo.name}成立于{brandInfo.founded}，是一个专注于萝莉风格与洛丽塔时尚文化的原创内容平台。我们致力于为热爱甜美穿搭的女孩们提供专业、实用、有趣的时尚指南。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                我们的团队成员都是资深洛丽塔爱好者，拥有丰富的穿搭经验和深厚的文化理解。从入门科普到进阶技巧，我们希望陪伴每一位女孩在萝莉风格的时尚之路上越走越远。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
            关于萝莉风格穿搭，你最关心的问题都在这里
          </p>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                >
                  <h4 className="text-sm md:text-base font-medium text-foreground-950 pr-4">{faq.question}</h4>
                  <i
                    className={`text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <p className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-accent-100/50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            订阅我们的<span className="text-primary-500">时尚通讯</span>
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            第一时间获取最新穿搭指南、洛丽塔文化活动资讯和独家搭配灵感
          </p>
          <NewsletterForm />
        </div>
      </section>
    </Layout>
  );
}